import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import axios from "axios"
import {
  Container,
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Grid,
  Paper,
  Divider,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  FormHelperText,
} from "@mui/material"
import {
  Assignment,
  RateReview,
  Feedback,
  Timeline,
  Person,
  AccessTime,
  AccountTree,
  Star,
  StarBorder,
  Visibility,
  Email,
  Send,
  Search,
  FilterList,
} from "@mui/icons-material"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider } from "@mui/x-date-pickers"
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    "Content-Type": "application/json",
  },
})

const FeedbackPage = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [feedback, setFeedback] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState({
    feedback: false,
    employees: false,
    submit: false,
  })
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })
  const [newFeedback, setNewFeedback] = useState({
    employee_id: "",
    employee_email: "",
    type: "Peer",
    comment: "",
    anonymous: false,
    rating: "",
  })

  // State for filtering feedback by employee
  const [selectedEmployeeForView, setSelectedEmployeeForView] = useState("")

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([fetchEmployees(), fetchFeedback()])
      } catch (error) {
        //console.error("Error fetching initial data:", error)
      }
    }
    fetchInitialData()
  }, [])


  useEffect(() => {
    if (newFeedback.employee_id) {
      const selectedEmployee = employees.find((emp) => emp.employee_id === newFeedback.employee_id)
      if (selectedEmployee) {
        setNewFeedback((prev) => ({
          ...prev,
          employee_email: selectedEmployee.email || "",
        }))
      }
    } else {
      setNewFeedback((prev) => ({
        ...prev,
        employee_email: "",
      }))
    }
  }, [newFeedback.employee_id, employees])

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      let employee_id = payload.employee_id;
      if (!employee_id) {
        try {
          const userRes = await api.get(`/api/user/current`, { headers: { Authorization: `Bearer ${token}` } });
          if (userRes.data && userRes.data.employee_id) employee_id = userRes.data.employee_id;
        } catch (e) { }
      }
      setLoading((prev) => ({ ...prev, employees: true }))
      const response = await api.get(`/api/feedback/employees?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,

          },

        }
      )

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid employee data received from server")
      }

      const employeeData = response.data

      const normalizedEmployees = employeeData.map((emp) => ({
        employee_id: emp.employee_id || emp.id || emp._id,
        firstName: emp.firstName || emp.first_name || emp.fname || "Unknown",
        lastName: emp.lastName || emp.last_name || emp.lname || "",
        email: emp.email || "",
        department: emp.department || emp.dept || "General",
        position: emp.position || emp.designation || emp.role || "",
      }))

      setEmployees(normalizedEmployees)
      return normalizedEmployees
    } catch (error) {
      //console.error("Error fetching employees:", error)
      openSnackbar(`Failed to load employees: ${error.message}`, "error")
      setEmployees([])
      return []
    } finally {
      setLoading((prev) => ({ ...prev, employees: false }))
    }
  }

  const fetchFeedback = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      let employee_id = payload.employee_id;
      if (!employee_id) {
        try {
          const userRes = await api.get(`/api/user/current`, { headers: { Authorization: `Bearer ${token}` } });
          if (userRes.data && userRes.data.employee_id) employee_id = userRes.data.employee_id;
        } catch (e) { }
      }
      setLoading((prev) => ({ ...prev, feedback: true }))
      const response = await api.get(`/api/feedback?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,

          }
        }
      )
      setFeedback(response.data || [])
      return response.data || []
    } catch (error) {
      //console.error("Error fetching feedback:", error)
      openSnackbar(`Failed to load feedback: ${error.message}`, "error")
      setFeedback([])
      return []
    } finally {
      setLoading((prev) => ({ ...prev, feedback: false }))
    }
  }

  const handleTabChange = (event, newValue) => {
    navigate(newValue)
  }

  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity })
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const handleSubmitFeedback = async () => {
    try {
      if (!newFeedback.employee_id || !newFeedback.comment || !newFeedback.rating) {
        openSnackbar("Employee, comments, and rating are required", "error");
        return;
      }

      setLoading(prev => ({ ...prev, submit: true }));

      // Get token
      const token = localStorage.getItem("token");

      let company_id = null;

      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          company_id = payload.company_id;
        } catch (err) {
          //console.error("Failed to decode token:", err);
        }
      }

      const feedbackPayload = {
        ...newFeedback,
        rating: Number.parseInt(newFeedback.rating),
      };

      const response = await api.post(
        "/api/feedback",
        feedbackPayload,
        {
          params: { company_id },

          headers: {
            Authorization: `Bearer ${token}`,
          },

          withCredentials: true,
        }
      );

      await fetchFeedback();

      openSnackbar(
        "Feedback submitted successfully and email notification sent!"
      );

      resetFeedbackForm();

    } catch (error) {
      //console.error("Submission failed:", error);


      openSnackbar(
        error.response?.data?.message || "Submission failed",
        "error"
      );

    } finally {
      setLoading(prev => ({ ...prev, submit: false }));
    }
  };


  const resetFeedbackForm = () => {
    setNewFeedback({
      employee_id: "",
      employee_email: "",
      type: "Peer",
      comment: "",
      anonymous: false,
      rating: "",
    })
  }

  const getStars = (rating) => {
    const ratingNum = Number.parseInt(rating) || 0
    return (
      <>
        {[...Array(5)].map((_, i) =>
          i < ratingNum ? <Star key={i} color="primary" fontSize="small" /> : <StarBorder key={i} fontSize="small" />,
        )}
      </>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const getEmployeeName = (employeeId) => {
    const employee = employees.find((emp) => emp.employee_id === employeeId)
    if (!employee) return "Unknown Employee"

    const lastName = employee.lastName && employee.lastName.trim() ? employee.lastName : ""
    return lastName ? `${employee.firstName} ${lastName}` : employee.firstName
  }

  const filteredFeedbackForDisplay = useMemo(() => {
    if (!feedback || feedback.length === 0) return []

    if (!selectedEmployeeForView) return []

    return feedback.filter((item) => item.employee_id === selectedEmployeeForView)
  }, [feedback, selectedEmployeeForView])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth={false} sx={{ maxWidth: 1500, p: 3, boxShadow: 3, mx: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Performance Management
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={location.pathname}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                backgroundColor: "#f5f5f5",
                "& .MuiTab-root": { fontWeight: "medium", py: 2 },
              }}
            >
              <Tab icon={<Assignment />} label="Goals & Objectives" value="/performance" />
              <Tab icon={<RateReview />} label="Performance Reviews" value="/review" />
              <Tab icon={<Feedback />} label="Feedback System" value="/feedback" />
              <Tab icon={<Timeline />} label="Improvement Plans" value="/improve" />
              <Tab icon={<AccountTree />} label="Succession Planning" value="/succession" />
              <Tab icon={<Visibility />} label="View Status" value="/performancestatus" />
            </Tabs>
          </Box>
        </Paper>

        {location.pathname === "/feedback" && (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>

              <Grid item xs={12} md={4}>
                <Card elevation={3}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", color: "#1976d2" }}
                    >
                      <Feedback sx={{ mr: 1 }} /> Provide Feedback
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Tooltip title="Select the employee to provide feedback for" arrow>
                      <FormControl fullWidth margin="normal" required>
                        <InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Person sx={{ mr: 0.5, fontSize: 16 }} />
                            Employee Name *
                          </Box>
                        </InputLabel>
                        <Select
                          value={newFeedback.employee_id}
                          onChange={(e) => setNewFeedback({ ...newFeedback, employee_id: e.target.value })}
                          label="Employee Name"
                          disabled={loading.employees}
                        >
                          <MenuItem value="">
                            <em>{loading.employees ? "Loading employees..." : "Select Employee"}</em>
                          </MenuItem>
                          {employees.length > 0
                            ? employees.map((employee) => (
                              <MenuItem key={employee.employee_id} value={employee.employee_id}>
                                {getEmployeeName(employee.employee_id)}

                              </MenuItem>
                            ))
                            : !loading.employees && (
                              <MenuItem disabled>
                                <em>No employees found</em>
                              </MenuItem>
                            )}
                        </Select>
                        {loading.employees && <CircularProgress sx={{ mt: 1 }} size={20} />}
                      </FormControl>
                    </Tooltip>


                    <Tooltip title="Company email address (automatically fetched from database)" arrow>
                      <TextField
                        fullWidth
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Email sx={{ mr: 0.5, fontSize: 16 }} />
                            Email
                          </Box>
                        }
                        value={newFeedback.employee_email}
                        margin="normal"
                        disabled
                        helperText="Email is automatically fetching based on employee name"
                        InputProps={{
                          style: { backgroundColor: "#f5f5f5" },
                        }}
                      />
                    </Tooltip>

                    <FormControl fullWidth margin="normal">
                      <InputLabel>Feedback Type</InputLabel>
                      <Select
                        value={newFeedback.type}
                        onChange={(e) => setNewFeedback({ ...newFeedback, type: e.target.value })}
                        label="Feedback Type"
                      >
                        <MenuItem value="Manager">Manager Feedback</MenuItem>
                        <MenuItem value="Peer">Peer Feedback</MenuItem>
                        <MenuItem value="Self">Self-Assessment</MenuItem>
                        <MenuItem value="Client">Client Feedback</MenuItem>
                        <MenuItem value="Team">Team Feedback</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Feedback Comments"
                      value={newFeedback.comment}
                      onChange={(e) => setNewFeedback({ ...newFeedback, comment: e.target.value })}
                      margin="normal"
                      placeholder="Provide detailed, constructive feedback..."
                      required
                    />

                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography component="legend">Rating *</Typography>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {[1, 2, 3, 4, 5].map((value) => (
                          <IconButton
                            key={value}
                            onClick={() => setNewFeedback({ ...newFeedback, rating: value })}
                            color={newFeedback.rating >= value ? "primary" : "default"}
                          >
                            <Star />
                          </IconButton>
                        ))}
                      </Box>
                    </Box>



                    <Box sx={{ mt: 3, textAlign: "center" }}>
                      <Tooltip title="Submit feedback " arrow>
                        <Button
                          variant="contained"
                          startIcon={loading.submit ? <CircularProgress size={20} /> : <Send />}
                          onClick={handleSubmitFeedback}
                          disabled={loading.submit}
                          sx={{
                            bgcolor: "#1976d2",
                            color: "white",
                            px: 4,
                            py: 1.5,
                            fontSize: "1rem",
                            fontWeight: "medium",
                          }}
                        >
                          {loading.submit ? "Submitting..." : "Submit  "}
                        </Button>
                      </Tooltip>
                      <FormHelperText sx={{ mt: 1, textAlign: "center", fontSize: "0.875rem" }}>
                        This will submit the feedback and send an email notification to the employee
                      </FormHelperText>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>


              <Grid item xs={12} md={8}>
                <Paper elevation={3}>
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                      <Search sx={{ mr: 1 }} /> Employee Feedback
                      {selectedEmployeeForView && ` (${filteredFeedbackForDisplay.length})`}
                    </Typography>
                    <Tooltip title="Select an employee to view their feedback" arrow>
                      <FormControl size="small" sx={{ minWidth: 310 }}>
                        <InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <FilterList sx={{ mr: 0.5, fontSize: 16 }} />
                            Select Employee to View Feedback
                          </Box>
                        </InputLabel>
                        <Select
                          value={selectedEmployeeForView}
                          onChange={(e) => setSelectedEmployeeForView(e.target.value)}
                          label="Select Employee to View Feedback"
                        >
                          <MenuItem value="">
                            <em>Choose an employee</em>
                          </MenuItem>
                          {employees.map((employee) => (
                            <MenuItem key={employee.employee_id} value={employee.employee_id}>
                              {getEmployeeName(employee.employee_id)}

                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Tooltip>
                  </Box>

                  <Divider />

                  {loading.feedback ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading feedback...
                      </Typography>
                    </Box>
                  ) : !selectedEmployeeForView ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <Search sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Select an Employee
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Choose an employee from the dropdown above to view their feedback.
                      </Typography>
                    </Box>
                  ) : filteredFeedbackForDisplay.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography variant="body1">
                        No feedback found for {getEmployeeName(selectedEmployeeForView)}.
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ maxHeight: "650px", overflow: "auto", p: 0 }}>
                      {filteredFeedbackForDisplay.map((item) => (
                        <React.Fragment key={item.id}>
                          <ListItem alignItems="flex-start" sx={{ "&:hover": { bgcolor: "#f9f9f9" }, p: 2 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
                                    {item.anonymous ? "Anonymous" : item.type} Feedback for{" "}
                                    {getEmployeeName(item.employee_id)}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                    <AccessTime fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                    <Typography variant="body2" color="text.secondary">
                                      Date: {formatDate(item.date)}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                      Rating:
                                    </Typography>
                                    {getStars(item.rating)}
                                  </Box>

                                  <Typography variant="body2" paragraph sx={{ mt: 1 }}>
                                    {item.comment}
                                  </Typography>
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                                <Chip label={item.type} color="info" size="small" sx={{ mb: 1 }} />
                                {item.anonymous && <Chip label="Anonymous" color="secondary" size="small" />}
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  )
}

export default FeedbackPage